import PropTypes from 'prop-types';
import { Spin } from 'antd';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
    ContentCardHeader,
    ContentCardTitle,
    ContentCardFooter,
    DetailLabel,
    DetailSecondaryText,
    ProfileSecurityCard,
    ProfileSecurityCardFooter,
    ProfilePasswordFieldsSection,
    ProfilePasswordFieldRow,
    ProfilePasswordInputWrap,
} from '@/pages/inside/tenant/style';
import { RotateLeftOutlined } from '@ant-design/icons';

const ProfilePasswordField = ({ label, children }) => (
    <ProfilePasswordFieldRow>
        <DetailLabel>{label}</DetailLabel>
        <ProfilePasswordInputWrap>{children}</ProfilePasswordInputWrap>
    </ProfilePasswordFieldRow>
);

ProfilePasswordField.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

const ProfilePasswordCard = ({
    passwordVerified,
    currentPassword,
    onCurrentPasswordChange,
    newPassword,
    onNewPasswordChange,
    confirmPassword,
    onConfirmPasswordChange,
    passwordVerifying,
    passwordSaving,
    onVerifyPassword,
    onModifyPassword,
    onReset,
}) => (
    <ProfileSecurityCard>
        <ContentCardHeader>
            <ContentCardTitle>Security</ContentCardTitle>
            <DetailSecondaryText as="p" style={{ margin: '8px 0 0', display: 'block' }}>
                Verify your current password before setting a new one.
            </DetailSecondaryText>
        </ContentCardHeader>

        <ProfilePasswordFieldsSection>
            <ProfilePasswordField label="Current password">
                <Input
                    isPassword
                    noAutofill
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => onCurrentPasswordChange(e.target.value)}
                    disabled={passwordVerified || passwordVerifying}
                    $maxWidth="100%"
                />
            </ProfilePasswordField>

            {passwordVerified && (
                <>
                    <ProfilePasswordField label="New password">
                        <Input
                            isPassword
                            noAutofill
                            placeholder="At least 6 characters"
                            value={newPassword}
                            onChange={(e) => onNewPasswordChange(e.target.value)}
                            $maxWidth="100%"
                        />
                    </ProfilePasswordField>
                    <ProfilePasswordField label="Confirm password">
                        <Input
                            isPassword
                            noAutofill
                            placeholder="Re-enter new password"
                            value={confirmPassword}
                            onChange={(e) => onConfirmPasswordChange(e.target.value)}
                            $maxWidth="100%"
                        />
                    </ProfilePasswordField>
                </>
            )}
        </ProfilePasswordFieldsSection>

        <ProfileSecurityCardFooter>
            <ContentCardFooter style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                {passwordVerified ? (
                    <Button 
                        type="reset" 
                        $primary={false} 
                        $size="14px" 
                        onClick={onReset} 
                        disabled={passwordSaving}
                    >
                        <RotateLeftOutlined /> Start over
                    </Button>
                ) : (
                    <span />
                )}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    {(passwordVerifying || passwordSaving) && <Spin size="small" />}
                    {!passwordVerified ? (
                        <Button
                            type="button"
                            $primary
                            $size="14px"
                            $padding="8px 20px"
                            onClick={onVerifyPassword}
                            disabled={passwordVerifying || !currentPassword.trim()}
                        >
                            Verify password
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            $primary
                            $size="14px"
                            $padding="8px 20px"
                            onClick={onModifyPassword}
                            disabled={passwordSaving || !newPassword || !confirmPassword}
                        >
                            Update password
                        </Button>
                    )}
                </div>
            </ContentCardFooter>
        </ProfileSecurityCardFooter>
    </ProfileSecurityCard>
);

ProfilePasswordCard.propTypes = {
    passwordVerified: PropTypes.bool.isRequired,
    currentPassword: PropTypes.string.isRequired,
    onCurrentPasswordChange: PropTypes.func.isRequired,
    newPassword: PropTypes.string.isRequired,
    onNewPasswordChange: PropTypes.func.isRequired,
    confirmPassword: PropTypes.string.isRequired,
    onConfirmPasswordChange: PropTypes.func.isRequired,
    passwordVerifying: PropTypes.bool.isRequired,
    passwordSaving: PropTypes.bool.isRequired,
    onVerifyPassword: PropTypes.func.isRequired,
    onModifyPassword: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
};

export default ProfilePasswordCard;
